<template>
  <SidebarNavigation :uri="uri">
    <div class="w-full mx-auto">
      <h2 class="flex justify-center font-bold text-xl sm:text-2xl mb-4 sm:mb-6 border-b-2 pb-3 sm:pb-4 px-4 sm:px-0">{{ __('Email Signature Generator') }}</h2>
      
      <div class="md:mt-10 sm:mt-3 justify-center flex flex-col lg:flex-row w-full mx-auto gap-4 lg:gap-6 px-4 sm:px-6 lg:px-0">
        <!-- Form Section -->
        <div class="card-form w-full lg:w-1/2 mx-auto">
          <div class="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h3 class="text-xl font-semibold mb-5 text-gray-800 flex items-center gap-2">
              <icon name="settings" class="w-5 h-5 text-green-600" />
              Signature Details
            </h3>

            <!-- Template Selection -->
            <div class="mb-6">
              <label class="block mb-2 text-sm font-semibold text-gray-700">Template Style</label>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <button
                  v-for="template in templates"
                  :key="template.id"
                  @click="selectedTemplate = template.id"
                  :class="[
                    'px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium',
                    selectedTemplate === template.id
                      ? 'border-green-600 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-green-400 text-gray-700'
                  ]"
                >
                  {{ template.name }}
                </button>
              </div>
            </div>

            <!-- Profile Photo -->
            <div class="mb-6 pb-6 border-b border-gray-200">
              <label class="block mb-3 text-sm font-semibold text-gray-700">Profile Photo</label>
              <div class="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div v-if="form.photo" class="relative group">
                  <div class="w-24 h-24 rounded-full overflow-hidden border-2 border-gray-300 shadow-md">
                    <img :src="form.photo" alt="Profile" class="w-full h-full object-cover" />
                  </div>
                  <button
                    @click="form.photo = null"
                    class="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                    title="Remove photo"
                  >
                    <icon name="close" class="w-4 h-4" />
                  </button>
                </div>
                <div v-else class="w-24 h-24 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                  <icon name="user" class="w-12 h-12 text-gray-400" />
                </div>
                <div class="flex flex-col gap-2">
                  <label class="px-4 py-2 bg-green-600 text-white rounded-lg cursor-pointer hover:bg-green-700 active:bg-green-800 transition-colors text-sm font-medium shadow-sm hover:shadow-md inline-flex items-center justify-center gap-2">
                    <input type="file" @change="handlePhotoUpload" class="hidden" accept="image/*" />
                    <icon name="camera" class="w-4 h-4" />
                    Upload Photo
                  </label>
                  <p class="text-xs text-gray-500">Recommended: 200x200px, square image</p>
                </div>
              </div>
            </div>

            <!-- Company Logo -->
            <div class="mb-6 pb-6 border-b border-gray-200">
              <label class="block mb-3 text-sm font-semibold text-gray-700">Company Logo <span class="text-gray-500 font-normal">(Optional)</span></label>
              <div class="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div v-if="form.logo" class="relative group">
                  <div class="h-16 border-2 border-gray-300 rounded-lg p-2 bg-white shadow-md">
                    <img :src="form.logo" alt="Logo" class="h-full object-contain" />
                  </div>
                  <button
                    @click="form.logo = null"
                    class="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                    title="Remove logo"
                  >
                    <icon name="close" class="w-4 h-4" />
                  </button>
                </div>
                <div v-else class="h-16 w-32 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 flex items-center justify-center">
                  <icon name="image" class="w-8 h-8 text-gray-400" />
                </div>
                <div class="flex flex-col gap-2">
                  <label class="px-4 py-2 bg-gray-600 text-white rounded-lg cursor-pointer hover:bg-gray-700 active:bg-gray-800 transition-colors text-sm font-medium shadow-sm hover:shadow-md inline-flex items-center justify-center gap-2">
                    <input type="file" @change="handleLogoUpload" class="hidden" accept="image/*" />
                    <icon name="image" class="w-4 h-4" />
                    Upload Logo
                  </label>
                  <p class="text-xs text-gray-500">Recommended: Transparent PNG, max height 60px</p>
                </div>
              </div>
            </div>

            <!-- Contact Information Section -->
            <div class="mb-6 pb-6 border-b border-gray-200">
              <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <icon name="contact" class="w-5 h-5 text-green-600" />
                Contact Information
              </h3>
              <div class="space-y-4">
                <text-input
                  v-model="form.name"
                  :label="__('Full Name')"
                  type="text"
                  class="w-full"
                  :error="form.errors.name"
                  maxlength="100"
                />
                <text-input
                  v-model="form.title"
                  :label="__('Job Title')"
                  type="text"
                  class="w-full"
                  :error="form.errors.title"
                  maxlength="100"
                />
                <text-input
                  v-model="form.company"
                  :label="__('Company')"
                  type="text"
                  class="w-full"
                  :error="form.errors.company"
                  maxlength="100"
                />
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <text-input
                    v-model="form.email"
                    :label="__('Email')"
                    type="email"
                    class="w-full"
                    :error="form.errors.email || validationErrors.email"
                    @blur="validateEmailField"
                    maxlength="255"
                  />
                  <text-input
                    v-model="form.phone"
                    :label="__('Phone')"
                    type="tel"
                    class="w-full"
                    :error="form.errors.phone || validationErrors.phone"
                    @blur="validatePhoneField"
                    maxlength="20"
                    pattern="[\d\s\-\+\(\)\.]{10,}"
                  />
                </div>
                <text-input
                  v-model="form.website"
                  :label="__('Website')"
                  type="url"
                  class="w-full"
                  placeholder="https://example.com"
                  :error="form.errors.website || validationErrors.website"
                  @blur="validateWebsiteField"
                  maxlength="255"
                />
              </div>
            </div>

            <!-- Social Links -->
            <div class="mb-6 border-t pt-4">
              <label class="block mb-3 text-sm font-semibold text-gray-700">Social Media Links</label>
              <div class="space-y-3">
                <div class="flex items-center gap-2">
                  <icon name="linkedin" class="w-5 h-5 text-gray-400" />
                  <text-input
                    v-model="form.social_linkedin"
                    label="LinkedIn"
                    type="url"
                    placeholder="https://linkedin.com/in/yourname"
                    class="flex-1"
                    maxlength="255"
                  />
                </div>
                <div class="flex items-center gap-2">
                  <icon name="twitter" class="w-5 h-5 text-gray-400" />
                  <text-input
                    v-model="form.social_twitter"
                    label="Twitter/X"
                    type="url"
                    placeholder="https://twitter.com/yourname"
                    class="flex-1"
                    maxlength="255"
                  />
                </div>
                <div class="flex items-center gap-2">
                  <icon name="facebook" class="w-5 h-5 text-gray-400" />
                  <text-input
                    v-model="form.social_facebook"
                    label="Facebook"
                    type="url"
                    placeholder="https://facebook.com/yourname"
                    class="flex-1"
                    maxlength="255"
                  />
                </div>
                <div class="flex items-center gap-2">
                  <icon name="instagram" class="w-5 h-5 text-gray-400" />
                  <text-input
                    v-model="form.social_instagram"
                    label="Instagram"
                    type="url"
                    placeholder="https://instagram.com/yourname"
                    class="flex-1"
                    maxlength="255"
                  />
                </div>
              </div>
            </div>

            <!-- Link-in-Bio Integration -->
            <div class="mb-6 border-t pt-4">
              <label class="flex items-center gap-2 mb-3 text-sm font-semibold text-gray-700">
                <input
                  type="checkbox"
                  v-model="form.include_qr"
                  class="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                Include QR Code (Link to Link-in-Bio page)
              </label>
              <div v-if="form.include_qr" class="mt-3">
                <text-input
                  v-model="form.linkinbio_url"
                  label="Link-in-Bio URL"
                  type="url"
                  placeholder="https://yoursite.com/link/your-slug"
                  class="w-full"
                  :error="validationErrors.linkinbio_url"
                  @blur="validateLinkInBioUrlField"
                  maxlength="255"
                />
                <p class="text-xs text-gray-500 mt-1">QR code will link to this URL</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Preview Section -->
        <div class="card-items flex w-full lg:w-1/2 mx-auto py-0 lg:sticky lg:top-6 lg:self-start">
          <div class="flex flex-col items-center w-full">
            <div class="w-full mb-4">
              <h3 class="text-xl font-semibold text-gray-800 flex items-center gap-2 mb-2">
                <icon name="aperture" class="w-5 h-5 text-green-600" />
                Live Preview
              </h3>
              <p class="text-xs text-gray-500">How your signature will appear in emails</p>
            </div>

            <!-- Preview Container -->
            <div class="w-full bg-gray-100 rounded-lg p-6 border-2 border-gray-300">
              <div class="bg-white rounded-lg p-4 shadow-inner border border-gray-200">
                <div class="email-preview" v-html="signatureHtml"></div>
              </div>
            </div>

            <!-- Copy Button -->
            <div class="w-full mt-6 pt-4 border-t border-gray-200">
              <button
                @click="copyToClipboard"
                :disabled="!signatureHtml"
                class="w-full bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl hover:from-green-700 hover:to-green-800 active:from-green-800 active:to-green-900 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
              >
                <icon v-if="!copied" name="file" class="w-5 h-5 text-white fill-white" />
                <icon v-else name="tick" class="w-5 h-5 text-white fill-white" />
                {{ copied ? 'Copied to Clipboard!' : 'Copy HTML to Clipboard' }}
              </button>
              <p v-if="copied" class="text-green-600 text-sm text-center mt-3 flex items-center justify-center gap-1 font-medium">
                <icon name="tick" class="w-4 h-4" />
                Successfully copied! Paste into your email client
              </p>
              <p v-else class="text-xs text-gray-500 text-center mt-2">
                Copy the HTML code and paste it into your email signature settings
              </p>
            </div>

            <!-- HTML Code Preview (Collapsible) -->
            <div class="w-full mt-4">
              <button
                @click="showCode = !showCode"
                class="w-full text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium text-gray-700 flex items-center justify-between"
              >
                <span>View HTML Code</span>
                <span class="text-gray-600">{{ showCode ? '▲' : '▼' }}</span>
              </button>
              <div v-if="showCode" class="mt-2 bg-gray-900 rounded-lg p-4 overflow-auto max-h-96">
                <pre class="text-xs text-green-400"><code v-html="escapeHtml(signatureHtml)"></code></pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </SidebarNavigation>
</template>

<script>
import Layout from '@/Shared/ToolLayout'
import TextInput from '@/Shared/TextInput'
import Icon from '@/Shared/Icon'
import SidebarNavigation from '../../Shared/SidebarNavigation.vue'
import { validateEmail, validateURL, validatePhone, validateFileSize, validateFileType } from '../../utils/validation.js'

export default {
  components: {
    SidebarNavigation,
    Icon,
    TextInput,
  },
  layout: Layout,
  props: {
    title: String,
    uri: {
      type: String,
      required: false
    },
  },
  remember: 'form',
  data() {
    return {
      selectedTemplate: 'professional',
      showCode: false,
      copied: false,
      qrCodeDataUrl: null,
      templates: [
        { id: 'professional', name: 'Professional' },
        { id: 'minimal', name: 'Minimal' },
        { id: 'compact', name: 'Compact' }
      ],
      form: this.$inertia.form({
        name: '',
        title: '',
        company: '',
        email: '',
        phone: '',
        website: '',
        photo: null,
        logo: null,
        social_linkedin: '',
        social_twitter: '',
        social_facebook: '',
        social_instagram: '',
        include_qr: false,
        linkinbio_url: '',
      }),
      validationErrors: {},
    }
  },
  computed: {
    signatureHtml() {
      return this.generateSignatureHtml()
    }
  },
  watch: {
    'form.include_qr'(newVal) {
      if (newVal && this.form.linkinbio_url) {
        this.generateQrCode()
      }
    },
    'form.linkinbio_url'(newVal) {
      if (newVal && this.form.include_qr) {
        this.generateQrCode()
      }
    }
  },
  methods: {
    validateEmailField() {
      if (this.form.email) {
        const result = validateEmail(this.form.email)
        if (!result.valid) {
          this.validationErrors.email = result.message
        } else {
          delete this.validationErrors.email
        }
      } else {
        delete this.validationErrors.email
      }
    },
    validatePhoneField() {
      if (this.form && this.form.phone) {
        const result = validatePhone(this.form.phone)
        if (!result.valid) {
          this.validationErrors.phone = result.message
        } else {
          delete this.validationErrors.phone
        }
      } else {
        delete this.validationErrors.phone
      }
    },
    validateWebsiteField() {
      if (this.form && this.form.website) {
        const result = validateURL(this.form.website)
        if (!result.valid) {
          this.validationErrors.website = result.message
        } else {
          delete this.validationErrors.website
          // Normalize URL
          if (result.normalized) {
            this.form.website = result.normalized
          }
        }
      } else {
        delete this.validationErrors.website
      }
    },
    validateLinkInBioUrlField() {
      if (this.form && this.form.linkinbio_url) {
        const result = validateURL(this.form.linkinbio_url)
        if (!result.valid) {
          this.validationErrors.linkinbio_url = result.message
        } else {
          delete this.validationErrors.linkinbio_url
          // Normalize URL
          if (result.normalized) {
            this.form.linkinbio_url = result.normalized
          }
        }
      } else {
        delete this.validationErrors.linkinbio_url
      }
    },
    validateFileUpload(file, type = 'image') {
      if (!file) return { valid: true }
      
      const sizeResult = validateFileSize(file, 5)
      if (!sizeResult.valid) {
        if (window.$toast) {
          window.$toast.error(sizeResult.message)
        }
        return sizeResult
      }
      
      if (type === 'image') {
        const typeResult = validateFileType(file)
        if (!typeResult.valid) {
          if (window.$toast) {
            window.$toast.error(typeResult.message)
          }
          return typeResult
        }
      }
      
      return { valid: true }
    },
    handlePhotoUpload(event) {
      const file = event.target.files[0]
      if (file) {
        const validation = this.validateFileUpload(file, 'image')
        if (!validation.valid) {
          event.target.value = ''
          return
        }
        
        const reader = new FileReader()
        reader.onload = (e) => {
          this.form.photo = e.target.result
          if (window.$toast) {
            window.$toast.success('Photo uploaded successfully')
          }
        }
        reader.onerror = () => {
          if (window.$toast) {
            window.$toast.error('Failed to read file. Please try again.')
          }
          event.target.value = ''
        }
        reader.onabort = () => {
          if (window.$toast) {
            window.$toast.warning('File reading was cancelled')
          }
        }
        reader.readAsDataURL(file)
      }
    },
    handleLogoUpload(event) {
      const file = event.target.files[0]
      if (file) {
        const validation = this.validateFileUpload(file, 'image')
        if (!validation.valid) {
          event.target.value = ''
          return
        }
        
        const reader = new FileReader()
        reader.onload = (e) => {
          this.form.logo = e.target.result
          if (window.$toast) {
            window.$toast.success('Logo uploaded successfully')
          }
        }
        reader.onerror = () => {
          if (window.$toast) {
            window.$toast.error('Failed to read file. Please try again.')
          }
          event.target.value = ''
        }
        reader.onabort = () => {
          if (window.$toast) {
            window.$toast.warning('File reading was cancelled')
          }
        }
        reader.readAsDataURL(file)
      }
    },
    async generateQrCode() {
      if (!this.form.linkinbio_url) return
      
      try {
        const response = await this.axios.post(this.route('generate.qr'), {
          text: this.form.linkinbio_url,
          size: 120,
          error_correction: 'M',
          foreground_color: '#000000',
          background_color: '#FFFFFF'
        })
        
        if (response.data && response.data.qr_code_url) {
          this.qrCodeDataUrl = response.data.qr_code_url
        }
      } catch (error) {
        console.error('Error generating QR code:', error)
      }
    },
    generateSignatureHtml() {
      if (!this.form) return ''
      
      const template = this.templates.find(t => t.id === this.selectedTemplate)
      if (!template) return ''

      let html = ''

      switch (this.selectedTemplate) {
        case 'professional':
          html = this.generateProfessionalTemplate()
          break
        case 'minimal':
          html = this.generateMinimalTemplate()
          break
        case 'compact':
          html = this.generateCompactTemplate()
          break
      }

      return html
    },
    generateProfessionalTemplate() {
      if (!this.form) return ''
      
      // Professional: Logo on left, text on right
      let html = '<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif; font-size: 12px; color: #333333; line-height: 1.6;">'
      html += '<tr>'
      
      // Left column: Logo/Photo
      html += '<td valign="top" style="padding-right: 20px; padding-bottom: 10px;">'
      if (this.form.photo) {
        html += `<img src="${this.form.photo}" alt="${this.form.name || 'Photo'}" style="width: 80px; height: 80px; border-radius: 50%; border: 2px solid #e5e7eb; display: block;" />`
      } else if (this.form.logo) {
        html += `<img src="${this.form.logo}" alt="${this.form.company || 'Logo'}" style="max-height: 60px; max-width: 150px; display: block;" />`
      }
      html += '</td>'
      
      // Right column: Text content
      html += '<td valign="top" style="padding-bottom: 10px;">'
      
      // Name
      if (this.form.name) {
        html += `<div style="font-size: 16px; font-weight: bold; color: #1f2937; margin-bottom: 4px;">${this.escapeHtml(this.form.name)}</div>`
      }
      
      // Title
      if (this.form.title) {
        html += `<div style="font-size: 13px; color: #4b5563; margin-bottom: 6px;">${this.escapeHtml(this.form.title)}</div>`
      }
      
      // Company
      if (this.form.company) {
        html += `<div style="font-size: 12px; color: #6b7280; margin-bottom: 8px;">${this.escapeHtml(this.form.company)}</div>`
      }
      
      // Contact info
      html += '<div style="font-size: 11px; color: #6b7280; line-height: 1.8;">'
      if (this.form.email) {
        html += `<div>📧 <a href="mailto:${this.escapeHtml(this.form.email)}" style="color: #059669; text-decoration: none;">${this.escapeHtml(this.form.email)}</a></div>`
      }
      if (this.form.phone) {
        html += `<div>📞 <a href="tel:${this.escapeHtml(this.form.phone)}" style="color: #059669; text-decoration: none;">${this.escapeHtml(this.form.phone)}</a></div>`
      }
      if (this.form.website) {
        const website = this.formatUrl(this.form.website)
        html += `<div>🌐 <a href="${website}" style="color: #059669; text-decoration: none;" target="_blank">${this.escapeHtml(this.form.website)}</a></div>`
      }
      html += '</div>'
      
      // Social links
      const socialLinks = this.getSocialLinks()
      if (socialLinks.length > 0) {
        html += '<div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #e5e7eb;">'
        socialLinks.forEach(link => {
          html += `<a href="${link.url}" style="margin-right: 8px; text-decoration: none;" target="_blank">${link.icon}</a>`
        })
        html += '</div>'
      }
      
      // QR Code
      if (this.form.include_qr && this.qrCodeDataUrl) {
        html += '<div style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #e5e7eb;">'
        html += `<img src="${this.qrCodeDataUrl}" alt="QR Code" style="width: 80px; height: 80px; display: block;" />`
        html += '</div>'
      }
      
      html += '</td>'
      html += '</tr>'
      html += '</table>'
      
      return html
    },
    generateMinimalTemplate() {
      // Minimal: Centered layout
      let html = '<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif; font-size: 12px; color: #333333; line-height: 1.6; margin: 0 auto; text-align: center;">'
      html += '<tr><td style="text-align: center; padding-bottom: 10px;">'
      
      // Photo/Logo centered
      if (this.form.photo) {
        html += `<img src="${this.form.photo}" alt="${this.form.name || 'Photo'}" style="width: 60px; height: 60px; border-radius: 50%; border: 2px solid #e5e7eb; display: inline-block; margin-bottom: 8px;" />`
      } else if (this.form.logo) {
        html += `<img src="${this.form.logo}" alt="${this.form.company || 'Logo'}" style="max-height: 50px; max-width: 120px; display: inline-block; margin-bottom: 8px;" />`
      }
      
      html += '<div style="text-align: center;">'
      
      // Name
      if (this.form.name) {
        html += `<div style="font-size: 15px; font-weight: bold; color: #1f2937; margin-bottom: 3px;">${this.escapeHtml(this.form.name)}</div>`
      }
      
      // Title
      if (this.form.title) {
        html += `<div style="font-size: 12px; color: #4b5563; margin-bottom: 5px;">${this.escapeHtml(this.form.title)}</div>`
      }
      
      // Company
      if (this.form.company) {
        html += `<div style="font-size: 11px; color: #6b7280; margin-bottom: 8px;">${this.escapeHtml(this.form.company)}</div>`
      }
      
      // Contact info
      html += '<div style="font-size: 11px; color: #6b7280; line-height: 1.6;">'
      if (this.form.email) {
        html += `<div><a href="mailto:${this.escapeHtml(this.form.email)}" style="color: #059669; text-decoration: none;">${this.escapeHtml(this.form.email)}</a></div>`
      }
      if (this.form.phone) {
        html += `<div><a href="tel:${this.escapeHtml(this.form.phone)}" style="color: #059669; text-decoration: none;">${this.escapeHtml(this.form.phone)}</a></div>`
      }
      if (this.form.website) {
        const website = this.formatUrl(this.form.website)
        html += `<div><a href="${website}" style="color: #059669; text-decoration: none;" target="_blank">${this.escapeHtml(this.form.website)}</a></div>`
      }
      html += '</div>'
      
      // Social links
      const socialLinks = this.getSocialLinks()
      if (socialLinks.length > 0) {
        html += '<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">'
        socialLinks.forEach(link => {
          html += `<a href="${link.url}" style="margin: 0 6px; text-decoration: none;" target="_blank">${link.icon}</a>`
        })
        html += '</div>'
      }
      
      // QR Code
      if (this.form.include_qr && this.qrCodeDataUrl) {
        html += '<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">'
        html += `<img src="${this.qrCodeDataUrl}" alt="QR Code" style="width: 60px; height: 60px; display: inline-block;" />`
        html += '</div>'
      }
      
      html += '</div>'
      html += '</td></tr></table>'
      
      return html
    },
    generateCompactTemplate() {
      if (!this.form) return ''
      
      // Compact: Single line with minimal spacing
      let html = '<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif; font-size: 11px; color: #333333; line-height: 1.4;">'
      html += '<tr>'
      
      // Small photo/logo
      html += '<td valign="middle" style="padding-right: 12px;">'
      if (this.form.photo) {
        html += `<img src="${this.form.photo}" alt="${this.form.name || 'Photo'}" style="width: 40px; height: 40px; border-radius: 50%; border: 1px solid #e5e7eb; display: block;" />`
      } else if (this.form.logo) {
        html += `<img src="${this.form.logo}" alt="${this.form.company || 'Logo'}" style="max-height: 30px; max-width: 80px; display: block;" />`
      }
      html += '</td>'
      
      // Compact text
      html += '<td valign="middle" style="padding-right: 12px;">'
      
      if (this.form.name) {
        html += `<span style="font-weight: bold; color: #1f2937;">${this.escapeHtml(this.form.name)}</span>`
        if (this.form.title) {
          html += ` <span style="color: #6b7280;">|</span> <span style="color: #4b5563;">${this.escapeHtml(this.form.title)}</span>`
        }
        html += '<br>'
      }
      
      html += '<span style="color: #6b7280; font-size: 10px;">'
      if (this.form.email) {
        html += `<a href="mailto:${this.escapeHtml(this.form.email)}" style="color: #059669; text-decoration: none;">${this.escapeHtml(this.form.email)}</a>`
      }
      if (this.form.phone) {
        if (this.form.email) html += ' | '
        html += `<a href="tel:${this.escapeHtml(this.form.phone)}" style="color: #059669; text-decoration: none;">${this.escapeHtml(this.form.phone)}</a>`
      }
      if (this.form.website) {
        const website = this.formatUrl(this.form.website)
        if (this.form.email || this.form.phone) html += ' | '
        html += `<a href="${website}" style="color: #059669; text-decoration: none;" target="_blank">${this.escapeHtml(this.form.website)}</a>`
      }
      html += '</span>'
      
      html += '</td>'
      
      // QR Code (small)
      if (this.form.include_qr && this.qrCodeDataUrl) {
        html += '<td valign="middle">'
        html += `<img src="${this.qrCodeDataUrl}" alt="QR Code" style="width: 50px; height: 50px; display: block;" />`
        html += '</td>'
      }
      
      html += '</tr></table>'
      
      return html
    },
    getSocialLinks() {
      if (!this.form) return []
      
      const links = []
      const icons = {
        linkedin: '🔗',
        twitter: '🐦',
        facebook: '📘',
        instagram: '📷'
      }
      
      if (this.form.social_linkedin) {
        links.push({ url: this.formatUrl(this.form.social_linkedin), icon: icons.linkedin })
      }
      if (this.form.social_twitter) {
        links.push({ url: this.formatUrl(this.form.social_twitter), icon: icons.twitter })
      }
      if (this.form.social_facebook) {
        links.push({ url: this.formatUrl(this.form.social_facebook), icon: icons.facebook })
      }
      if (this.form.social_instagram) {
        links.push({ url: this.formatUrl(this.form.social_instagram), icon: icons.instagram })
      }
      
      return links
    },
    formatUrl(url) {
      if (!url) return '#'
      if (url.indexOf('://') === -1) {
        return 'https://' + url
      }
      return url
    },
    escapeHtml(text) {
      if (!text) return ''
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    },
    async copyToClipboard() {
      if (!this.signatureHtml) {
        if (window.$toast) {
          window.$toast.warning('Please generate a signature first')
        }
        return
      }

      this.copied = false

      try {
        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.signatureHtml)
        } else {
          // Fallback for older browsers
          const textarea = document.createElement('textarea')
          textarea.value = this.signatureHtml
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }
        
        this.copied = true
        if (window.$toast) {
          window.$toast.success('Signature copied to clipboard!')
        }
        setTimeout(() => {
          this.copied = false
        }, 3000)
      } catch (error) {
        console.error('Failed to copy:', error)
        if (window.$toast) {
          window.$toast.error('Failed to copy to clipboard. Please select and copy manually.')
        }
        this.copied = false
      }
    }
  },
  mounted() {
    // Load default values from business card if available
    // This could be enhanced to sync with actual business card data
  }
}
</script>

<style scoped>
.email-preview {
  max-width: 100%;
  overflow-x: auto;
}

.email-preview table {
  max-width: 100%;
}

.email-preview img {
  max-width: 100%;
  height: auto;
}
</style>

